import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingCart, Menu, X, ChevronDown, Globe, LogOut, LayoutDashboard } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";

const LOGO = "/assets/kreyola-logo.webp";

function TopBar() {
  const { lang, setLang, t } = useLang();
  return (
    <div className="bg-navy text-cream" data-testid="top-bar">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-2 text-[11px] sm:text-xs">
        <div className="flex items-center gap-2 font-medium tracking-wide">
          <span className="hidden sm:inline">🇺🇸</span>
          <span>{lang === "fr" ? "Livré partout aux États-Unis" : "Delivered across the United States"}</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle-btn"
            onClick={() => setLang(lang === "fr" ? "en" : "fr")}
            className="flex items-center gap-1.5 rounded-full border border-white/20 px-2.5 py-1 font-semibold transition hover:border-gold hover:text-gold"
          >
            <Globe size={13} /> {lang === "fr" ? "FR" : "EN"}
          </button>
        </div>
      </div>
    </div>
  );
}

const navLinks = [
  { to: "/", key: "nav.home" },
  { to: "/shop", key: "nav.shop" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
  { to: "/track-order", key: "tracking.title" },
];

export default function Navbar() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { count: wCount } = useWishlist();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/shop?search=${encodeURIComponent(q.trim())}`); setMobileOpen(false); }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "glass card-shadow" : "bg-cream-card"}`} data-testid="navbar">
      <TopBar />
      <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-3">
        <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="menu">
          <Menu className="text-navy" />
        </button>

        <Link to="/" className="flex shrink-0 items-center gap-2" data-testid="logo-link">
          <img src={LOGO} alt="Kreyola Boutik" className="h-12 w-12 object-contain sm:h-14 sm:w-14" />
          <span className="hidden font-display text-xl font-bold text-navy sm:block">Kreyola <span className="text-gradient-gold">Boutik</span></span>
        </Link>

        <nav className="ml-4 hidden items-center gap-4 lg:flex" data-testid="desktop-nav">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key.split(".")[1]}-link`}
              className={({ isActive }) =>
                `link-underline text-sm font-semibold text-navy/80 transition hover:text-navy ${isActive ? "active text-navy" : ""}`}
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={doSearch} className="ml-auto hidden flex-1 max-w-xs items-center md:flex" data-testid="search-form">
          <div className="relative w-full">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.search")}
              className="w-full rounded-full border border-border bg-cream py-2 pl-9 pr-4 text-sm outline-none transition focus:border-gold"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-1 md:ml-3">
          <div className="relative" onMouseEnter={() => setAccountOpen(true)} onMouseLeave={() => setAccountOpen(false)}>
            <button
              data-testid="account-btn"
              onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/account") : "/login")}
              className="grid h-10 w-10 place-items-center rounded-full text-navy transition hover:bg-cream"
              aria-label="account"
            >
              <User size={20} />
            </button>
            {accountOpen && (
              <div className="absolute right-0 top-full w-52 rounded-xl border border-border bg-cream-card p-2 card-shadow-lg" data-testid="account-menu">
                {user ? (
                  <>
                    <p className="px-3 py-2 text-xs text-muted-foreground">{user.name || user.email}</p>
                    <Link to={user.role === "admin" ? "/admin" : "/account"} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-navy hover:bg-cream">
                      <LayoutDashboard size={15} /> {t(user.role === "admin" ? "nav.admin" : "nav.dashboard")}
                    </Link>
                    <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-navy hover:bg-cream">
                      <LogOut size={15} /> {t("nav.logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block rounded-lg px-3 py-2 text-sm font-medium text-navy hover:bg-cream">{t("nav.login")}</Link>
                    <Link to="/register" className="block rounded-lg px-3 py-2 text-sm font-medium text-navy hover:bg-cream">{t("nav.register")}</Link>
                  </>
                )}
              </div>
            )}
          </div>

          <Link to="/account?tab=wishlist" data-testid="wishlist-btn" className="relative grid h-10 w-10 place-items-center rounded-full text-navy transition hover:bg-cream" aria-label="wishlist">
            <Heart size={20} />
            {wCount > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-sunset text-[10px] font-bold text-white">{wCount}</span>}
          </Link>

          <button onClick={() => setOpen(true)} data-testid="cart-btn" className="relative grid h-10 w-10 place-items-center rounded-full text-navy transition hover:bg-cream" aria-label="cart">
            <ShoppingCart size={20} />
            {count > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-gold text-[10px] font-bold text-navy">{count}</span>}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-navy/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm overflow-y-auto bg-cream-card p-5 animate-fade-in">
            <div className="mb-6 flex items-center justify-between">
              <img src={LOGO} alt="Kreyola" className="h-12 w-12 object-contain" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn"><X className="text-navy" /></button>
            </div>
            <form onSubmit={doSearch} className="mb-5">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("nav.search")} className="w-full rounded-full border border-border bg-cream py-2.5 pl-9 pr-4 text-sm outline-none" />
              </div>
            </form>
            <nav className="flex flex-col">
              {navLinks.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="border-b border-border py-3 font-head font-semibold text-navy">
                  {t(l.key)}
                </NavLink>
              ))}
            </nav>
            <div className="mt-6 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setMobileOpen(false)} className="btn-gold rounded-full py-2.5 text-center text-sm font-semibold">{t(user.role === "admin" ? "nav.admin" : "nav.dashboard")}</Link>
                  <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="rounded-full border border-border py-2.5 text-sm font-semibold text-navy">{t("nav.logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-gold rounded-full py-2.5 text-center text-sm font-semibold">{t("nav.login")}</Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="rounded-full border border-border py-2.5 text-center text-sm font-semibold text-navy">{t("nav.register")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
